import {
    type FC,
    type ReactNode,
    useActionState,
    useEffect,
    useState,
} from "react";
import { Icon, ToastTypeEnum, useToastCreate } from "@onshopfront/components";
import { isRecord } from "../../../types/internal/utility";
import { type DeveloperDispatch, useDeveloperSelector } from "../../_Bootstrap/hooks";
import { store } from "../../_Bootstrap/RoutingAndStore";
import { updateUserDetails } from "../../Actions/UserActions";
import Button from "../../Common/Button";
import Input from "../../Common/Input/Input";
import { useTitle } from "../../Utilities/TitleHook";

const AccountUpdateAction = async (
    _: null | string | true,
    formData: FormData
): Promise<null | string | true> => {
    // Validate email
    const email = formData.get("email");

    if(typeof email !== "string" || !email.trim()) {
        return "Please enter a valid email";
    }

    // Validate name
    const name = formData.get("name");

    if(typeof name !== "string" || !name.trim()) {
        return "Please enter a name";
    }

    try {
        const response = await fetch("/user", {
            method     : "POST",
            credentials: "same-origin",
            headers    : new Headers({
                "Accept"      : "application/json",
                "Content-Type": "application/json",
            }),
            body: JSON.stringify({
                email,
                name,
            }),
        });

        if(!response.ok) {
            throw await response.json();
        }

        (store.dispatch as DeveloperDispatch)(updateUserDetails({
            email,
            name,
        }));

        return null;
    } catch(err) {
        if(isRecord(err)) {
            if("message" in err && typeof err.message === "string") {
                return err.message;
            }
        }

        console.error("Error updating account details", err);
    }

    return "Error updating account details";
};

const Account: FC = () => {
    const [ message, updateDetails, isPending ] = useActionState(AccountUpdateAction, null);

    useTitle("Account Details");

    const user = useDeveloperSelector(state => state.user.user);

    const createToast = useToastCreate();

    const [ loading, setLoading ] = useState(true);
    const [ error, setError ] = useState(false);
    const [ email, setEmail ] = useState("");
    const [ name, setName ] = useState("");

    useEffect(() => {
        if(!message) {
            return;
        }

        if(message === true) {
            createToast({
                type   : ToastTypeEnum.Success,
                message: "Successfully updated account details",
            });
        } else {
            createToast({
                type: ToastTypeEnum.Error,
                message,
            });
        }
    }, [ message, createToast ]);

    useEffect(() => {
        if(typeof user === "boolean") {
            return;
        }

        setLoading(true);
        setError(false);

        fetch("/user")
            .then(async response => {
                if(!response.ok) {
                    throw await response.json();
                }

                return response.json();
            })
            .then(user => {
                setEmail(user.email);
                setName(user.name);
            })
            .catch(err => {
                console.error("Failed to get the current user", err);
                setError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [ user ]);

    let content: ReactNode;

    if(loading) {
        content = (
            <p>We are loading your account details...</p>
        );
    } else if(error || !user) {
        content = (
            <p>An error occurred while getting the account details</p>
        );
    } else {
        content = (
            <form
                action={updateDetails}
                className="max-w-2xl"
            >
                <Input
                    icon="at"
                    label="Email"
                    type="email"
                    name="email"
                    value={email}
                    onChange={e => {
                        setEmail(e.currentTarget.value);
                    }}
                />
                <Input
                    icon="id-card"
                    label="Name"
                    type="text"
                    name="name"
                    value={name}
                    onChange={e => {
                        setName(e.currentTarget.value);
                    }}
                />
                <Button
                    type="submit"
                    classes={[ "w-full mt-8" ]}
                    disabled={isPending}
                >
                    {(isPending ?
                        <Icon icon={[ "fal", "spinner-third" ]} spin /> :
                        "Update"
                    )}
                </Button>
            </form>
        );
    }

    return (
        <main className="p-8 box-border">
            <h1 className="text-4xl">
                Account Details
            </h1>
            <div className="mt-4">
                {content}
            </div>
        </main>
    );
};

export default Account;
